// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
import { DEFAULT_SNF_TRACE, defaultForbiddenPattern, parseBridgeAdmissionOutcome, parseBridgeAdmissionReceipt, parseBridgeLifecycle, parseRemainingWitnessUses, requireNonEmptyInput, requirePositiveInteger, } from "./seam-bridge-adapter-validation.js";
export function createSeamBridgeAdapter(w) {
    const seamTheorems = new Map();
    const witnessTheorems = new Map();
    const bridgeMetadata = new Map();
    const requireIssuedId = (id, kind) => {
        if (!Number.isSafeInteger(id) || id <= 0) {
            throw new Error(`WASM backend returned invalid ${kind} handle`);
        }
    };
    const requireKnownSeamTheorem = (seam) => {
        const theoremCode = seamTheorems.get(seam);
        if (theoremCode === undefined)
            throw new Error("Unknown seam handle");
        return theoremCode;
    };
    const requireKnownWitnessTheorem = (witness) => {
        const theoremCode = witnessTheorems.get(witness);
        if (theoremCode === undefined) {
            throw new Error("Unknown bridge witness handle");
        }
        return theoremCode;
    };
    const requireKnownBridgeTheorem = (bridge) => {
        const theoremCode = bridgeMetadata.get(bridge)?.theoremCode;
        if (theoremCode === undefined) {
            throw new Error("Unknown seam bridge handle");
        }
        return theoremCode;
    };
    const requireKnownBridgeMetadata = (bridge) => {
        const metadata = bridgeMetadata.get(bridge);
        if (metadata === undefined)
            throw new Error("Unknown seam bridge handle");
        return metadata;
    };
    const bridgeExpectations = (bridge, metadata, scope) => ({
        theoremCode: metadata.theoremCode,
        bridgeId: bridge,
        seamId: metadata.seamId,
        witnessId: metadata.witnessId,
        source: metadata.source,
        target: metadata.target,
        scope,
    });
    return {
        define: (theoremCode, forbiddenPattern = defaultForbiddenPattern(theoremCode)) => {
            const pattern = requireNonEmptyInput(forbiddenPattern, "forbidden pattern");
            const seam = w.seam_define(theoremCode, pattern);
            requireIssuedId(seam, "seam");
            seamTheorems.set(seam, theoremCode);
            return seam;
        },
        mintWitness: (theoremCode, scope, maxUses = 1) => {
            const witnessScope = requireNonEmptyInput(scope, "bridge witness scope");
            const witness = w.bridge_witness_mint(theoremCode, witnessScope, requirePositiveInteger(maxUses, "maxUses"));
            requireIssuedId(witness, "bridge witness");
            witnessTheorems.set(witness, theoremCode);
            return witness;
        },
        propose: (seam, witness, source, target) => {
            const theoremCode = requireKnownSeamTheorem(seam);
            if (witness !== null &&
                requireKnownWitnessTheorem(witness) !== theoremCode) {
                throw new Error("Bridge witness theorem does not match seam theorem");
            }
            const normalizedSource = requireNonEmptyInput(source, "bridge source");
            const normalizedTarget = requireNonEmptyInput(target, "bridge target");
            const bridge = w.bridge_propose(seam, witness ?? 0, normalizedSource, normalizedTarget);
            requireIssuedId(bridge, "seam bridge");
            bridgeMetadata.set(bridge, {
                theoremCode,
                seamId: seam,
                witnessId: witness,
                source: normalizedSource,
                target: normalizedTarget,
                admittedScope: null,
            });
            return bridge;
        },
        admit: (bridge, scope, phases = DEFAULT_SNF_TRACE) => {
            const metadata = requireKnownBridgeMetadata(bridge);
            const normalizedScope = requireNonEmptyInput(scope, "bridge admission scope");
            const outcome = parseBridgeAdmissionOutcome(w.bridge_admit(bridge, normalizedScope, JSON.stringify(phases), false), bridgeExpectations(bridge, metadata, normalizedScope));
            if (outcome.status === "admitted") {
                metadata.admittedScope = normalizedScope;
            }
            return outcome;
        },
        admitWithSquare: (bridge, scope, generatorCode, phases = DEFAULT_SNF_TRACE) => {
            const metadata = requireKnownBridgeMetadata(bridge);
            const normalizedScope = requireNonEmptyInput(scope, "bridge admission scope");
            const outcome = parseBridgeAdmissionOutcome(w.bridge_admit_square(bridge, normalizedScope, JSON.stringify(phases), generatorCode), bridgeExpectations(bridge, metadata, normalizedScope));
            if (outcome.status === "admitted") {
                metadata.admittedScope = normalizedScope;
            }
            return outcome;
        },
        receipt: (bridge) => {
            const metadata = requireKnownBridgeMetadata(bridge);
            if (metadata.admittedScope === null) {
                throw new Error("Bridge receipt requested before adapter admission");
            }
            return parseBridgeAdmissionReceipt(w.bridge_receipt(bridge), bridgeExpectations(bridge, metadata, metadata.admittedScope));
        },
        lifecycle: (bridge) => {
            requireKnownBridgeTheorem(bridge);
            return parseBridgeLifecycle(w.bridge_lifecycle(bridge));
        },
        remainingWitnessUses: (witness) => {
            requireKnownWitnessTheorem(witness);
            return parseRemainingWitnessUses(w.bridge_witness_remaining_uses(witness));
        },
        revokeWitness: (witness) => {
            requireKnownWitnessTheorem(witness);
            w.bridge_revoke_witness(witness);
        },
    };
}
//# sourceMappingURL=seam-bridge-adapter.js.map